'use client';

import { useState } from 'react';

import { DepositModal } from '../auth/DepositModalDynamic';
import { AuthModal } from '../auth/subcomponents/modal/AuthModal';
import { HowItWorksModal } from '../onboarding/HowItWorksModalDynamic';
import { InviteGateModal } from '../referrals/InviteGateModal';
import { ReviewPromptGate } from '../reviews';
import { SearchDropdown } from '../search/Dropdown/SearchDropdown';
import { PositionActionModal } from './PositionActionModal';

interface ModalsOrchestratorProps {
  auth: {
    isOpen: boolean;
    onClose: () => void;
    locked?: boolean;
  };
  deposit: {
    isOpen: boolean;
    onClose: () => void;
  };
  howItWorks: {
    isOpen: boolean;
    onClose: () => void;
  };
  search: {
    isOpen: boolean;
    onClose: () => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
    searchQuery?: string;
  };
  referral: {
    isOpen: boolean;
    onClose: () => void;
  };
}

/**
 * Defers a modal's first mount until it is first opened, then keeps it mounted.
 *
 * DepositModal and HowItWorksModal are next/dynamic(ssr:false), but that only
 * delays the chunk by a tick — a component that is *rendered* unconditionally
 * still downloads on every page. Worse, DepositModal's hooks run while closed,
 * so usePrivyPolBalance polled an RPC every 15s on every route.
 *
 * Mounting on first open (rather than gating on isOpen) is deliberate: both
 * modals observe the isOpen true->false transition while mounted — DepositModal
 * calls stopSessionRecording() in an else branch and resets via a wasOpenRef
 * effect — so unmounting on close would silently skip that. Once opened, the
 * component stays mounted and behaves exactly as before, close transition and
 * internal step state included.
 */
const useMountOnFirstOpen = (isOpen: boolean) => {
  const [hasOpened, setHasOpened] = useState(isOpen);
  if (isOpen && !hasOpened) setHasOpened(true);
  return hasOpened;
};

export const ModalsOrchestrator = ({
  auth,
  deposit,
  howItWorks,
  referral,
  search,
}: ModalsOrchestratorProps) => {
  const mountDeposit = useMountOnFirstOpen(deposit.isOpen);
  const mountHowItWorks = useMountOnFirstOpen(howItWorks.isOpen);

  return (
    <>
      <AuthModal isOpen={auth.isOpen} onClose={auth.onClose} locked={auth.locked} />
      {mountDeposit && <DepositModal isOpen={deposit.isOpen} onClose={deposit.onClose} />}
      {mountHowItWorks && (
        <HowItWorksModal isOpen={howItWorks.isOpen} onClose={howItWorks.onClose} />
      )}
      <PositionActionModal />

      <SearchDropdown
        isOpen={search.isOpen}
        onClose={search.onClose}
        anchorRef={search.anchorRef}
        searchQuery={search.searchQuery}
      />

      <InviteGateModal isOpen={referral.isOpen} onClose={referral.onClose} />

      {/* Experience-review prompt — self-contained; fires at 2nd trade / 2nd deposit */}
      <ReviewPromptGate />
    </>
  );
};
